import { Col, Row } from 'react-bootstrap';
import useIndexDBValue from 'src/app/hook/useIndexDBValue';
import { seachUserMetadata } from 'src/app/modules/auth/core/_requests';
import { SelectEditor, useSelectEditorManager } from 'src/app/modules/component/select-editor';
import { TimeEditor } from 'src/app/modules/component/time-editor/TimeEditor';
import { useTimeEditorManager } from 'src/app/modules/component/time-editor/useTimeEditorManager';
import { updateOrderGroupFields } from 'src/app/modules/phan-he-kham-benh/services/ChiDinhDVService';
import { IDanhSachDanhMuc } from 'src/app/modules/phan-he-tiep-nhan-thanh-toan/models/TiepDonModel';
import { KEY_DS_DANH_MUC_TIEP_DON } from 'src/app/modules/utils/Constant';
import { hasAuthority, KEY_AUTHORITIES } from 'src/app/modules/utils/Permission';
import LabelRequired from '../../../../../component/LabelRequired';

type Props = {
	thongTinPhieu: any;
	setThongTinPhieu?: React.Dispatch<React.SetStateAction<any>>;
	setDanhSachPhieu?: React.Dispatch<React.SetStateAction<any[]>>;
};

const ThongTinPhieu = ({ thongTinPhieu, setThongTinPhieu, setDanhSachPhieu }: Props) => {
	const allowEdit = hasAuthority(KEY_AUTHORITIES.UPDATE_FIELDS.PHIEU_CHI_DINH);

	const { value: danhSachDanhMuc } = useIndexDBValue<IDanhSachDanhMuc>({
		key: KEY_DS_DANH_MUC_TIEP_DON,
	});

	const onUpdate = (updatedData: any, field: string) => {
		if (!updatedData?.[field]) return;
		// Cập nhật thongTinPhieu với data mới
		setThongTinPhieu?.((prev: any) => ({
			...prev,
			[field]: updatedData[field]
		}));

		// Cập nhật trong danh sách phiếu
		setDanhSachPhieu?.((prevList: any[]) =>
			prevList.map((phieu: any) =>
				phieu.id === thongTinPhieu?.id
					? { ...phieu, [field]: updatedData[field] }
					: phieu
			)
		);
	}

	const doctorOrderDayManager = useTimeEditorManager({
		name: "doctorOrderDay",
		idFieldName: "orderGroupId",
		values: thongTinPhieu,
		updateFields: updateOrderGroupFields,
		onUpdateSuccess: (updatedData) => onUpdate(updatedData, "doctorOrderDay"),
	});

	const designatedDateManager = useTimeEditorManager({
		name: "designatedDate",
		idFieldName: "orderGroupId",
		values: thongTinPhieu,
		updateFields: updateOrderGroupFields,
		onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedDate"),
	});

	const indicationPersonSelectManager = useSelectEditorManager({
		name: "designatedPerson",
		idFieldName: "orderGroupId",
		values: thongTinPhieu,
		updateFields: updateOrderGroupFields,
		buildPayload: (option) => ({
			designatedPerson: option?.fullName,
			indicationTime: thongTinPhieu?.designatedDate
		}),
		onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedPerson"),
		enableConfirmWithReason: false, // Không dùng confirm with reason cho trường hình thức
	});

	const designatedPlaceSelectManager = useSelectEditorManager({
		name: "designatedPlace",
		idFieldName: "orderGroupId",
		values: thongTinPhieu,
		updateFields: updateOrderGroupFields,
		buildPayload: (option) => ({
			requestDeptCode: option?.code,
			requestDeptName: option?.name,
			designatedPlace: option?.name,
			indicationTime: thongTinPhieu?.designatedDate
		}),
		onUpdateSuccess: (updatedData) => onUpdate(updatedData, "designatedPlace"),
		enableConfirmWithReason: false, // Không dùng confirm with reason cho trường hình thức
	});

	return (
		<div className="px-3 py-1 bg-white border-bottom border-bottom-2">
			<Row>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Loại phiếu:" className="spaces min-w-110" />
					<span className="fw-light text-truncate">
						{thongTinPhieu?.type ||( thongTinPhieu?.id && 'Phiếu chỉ định') || '...'}
					</span>
				</Col>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Ngày y lệnh:" className="spaces min-w-110" />
					<TimeEditor
						name="doctorOrderDay"
						values={thongTinPhieu}
						isEditingTime={doctorOrderDayManager.isEditingTime}
						editedTime={doctorOrderDayManager.editedTime}
						showValidationConfirm={doctorOrderDayManager.showValidationConfirm}
						validationMessage={doctorOrderDayManager.validationMessage}
						onEdit={doctorOrderDayManager.handleEditTime}
						onCancel={doctorOrderDayManager.handleCancelEdit}
						onSave={doctorOrderDayManager.handleSaveTime}
						onConfirmValidation={doctorOrderDayManager.handleConfirmValidation}
						onCancelValidation={doctorOrderDayManager.handleCancelValidation}
						onTimeChange={doctorOrderDayManager.setEditedTime}
						allowEdit={allowEdit}
					/>
				</Col>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Ngày chỉ định:" className="spaces min-w-110" />
					<TimeEditor
						name="designatedDate"
						values={thongTinPhieu}
						isEditingTime={designatedDateManager.isEditingTime}
						editedTime={designatedDateManager.editedTime}
						showValidationConfirm={designatedDateManager.showValidationConfirm}
						validationMessage={designatedDateManager.validationMessage}
						onEdit={designatedDateManager.handleEditTime}
						onCancel={designatedDateManager.handleCancelEdit}
						onSave={designatedDateManager.handleSaveTime}
						onConfirmValidation={designatedDateManager.handleConfirmValidation}
						onCancelValidation={designatedDateManager.handleCancelValidation}
						onTimeChange={designatedDateManager.setEditedTime}
						allowEdit={allowEdit}
					/>
				</Col>
			</Row>

			<Row>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Mã phiếu:" className="spaces min-w-110" />
					<span className="fw-light text-truncate">{thongTinPhieu?.code || '...'}</span>
				</Col>
				<Col xs={8} className="d-flex align-items-center mb-3">
					<LabelRequired label="Nơi chỉ định:" className="spaces min-w-110" />
					<SelectEditor
						name="designatedPlace"
						values={{
							designatedPlace: thongTinPhieu?.designatedPlace
						}}
						options={danhSachDanhMuc?.listPhongKham || []}
						inline={true}
						menuPlacement="top"
						isEditingSelect={designatedPlaceSelectManager.isEditingSelect}
						editedValue={designatedPlaceSelectManager.editedValue}
						showValidationConfirm={designatedPlaceSelectManager.showValidationConfirm}
						showConfirmWithReason={designatedPlaceSelectManager.showConfirmWithReason}
						validationMessage={designatedPlaceSelectManager.validationMessage}
						onEdit={designatedPlaceSelectManager.handleEditSelect}
						onCancel={designatedPlaceSelectManager.handleCancelEdit}
						onSave={designatedPlaceSelectManager.handleSaveSelect}
						onConfirmValidation={designatedPlaceSelectManager.handleConfirmValidation}
						onCancelValidation={designatedPlaceSelectManager.handleCancelValidation}
						onConfirmWithReason={designatedPlaceSelectManager.handleConfirmWithReason}
						onCancelWithReason={designatedPlaceSelectManager.handleCancelWithReason}
						onValueChange={designatedPlaceSelectManager.setEditedValue}
						allowEdit={allowEdit}
					/>
				</Col>
			</Row>

			<Row>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Người chỉ định:" className="spaces min-w-110" />
					<SelectEditor
						name="designatedPerson"
						values={{
							designatedPerson: thongTinPhieu?.designatedPerson
						}}
						options={[]}
						searchFunction={seachUserMetadata}
						searchObject={{ position: 1 }}
						urlData="data.content"
						getOptionLabel={(opt) => opt?.fullName || ""}
						inline={true}
						menuPlacement="top"
						isEditingSelect={indicationPersonSelectManager.isEditingSelect}
						editedValue={indicationPersonSelectManager.editedValue}
						showValidationConfirm={indicationPersonSelectManager.showValidationConfirm}
						showConfirmWithReason={indicationPersonSelectManager.showConfirmWithReason}
						validationMessage={indicationPersonSelectManager.validationMessage}
						onEdit={indicationPersonSelectManager.handleEditSelect}
						onCancel={indicationPersonSelectManager.handleCancelEdit}
						onSave={indicationPersonSelectManager.handleSaveSelect}
						onConfirmValidation={indicationPersonSelectManager.handleConfirmValidation}
						onCancelValidation={indicationPersonSelectManager.handleCancelValidation}
						onConfirmWithReason={indicationPersonSelectManager.handleConfirmWithReason}
						onCancelWithReason={indicationPersonSelectManager.handleCancelWithReason}
						onValueChange={indicationPersonSelectManager.setEditedValue}
						allowEdit={allowEdit}
					/>
				</Col>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Trạng thái:" className="spaces min-w-110" />
					<span className="fw-light text-truncate">
						{thongTinPhieu?.statusName || '...'}
					</span>
				</Col>
			</Row>

			<Row>
				<Col xs={4} className="d-flex align-items-center mb-3">
					<LabelRequired label="Kho:" className="spaces min-w-110" />
					<span className="fw-light text-truncate">
						{thongTinPhieu?.warehouseName || '...'}
					</span>
				</Col>
				<Col xs={8} className="d-flex align-items-center mb-3">
					<LabelRequired label="Chẩn đoán:" className="spaces min-w-110" />
					<span className="fw-light text-truncate">
						{thongTinPhieu?.provisionalDiagnosisName || '...'}
					</span>
				</Col>
			</Row>
		</div>
	);
};

export default ThongTinPhieu;
